import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {SettingsService} from './settings.service';
import {StorageService} from './storage.service';
import {BehaviorSubject, Observable, Subscription, of} from 'rxjs';
import {catchError, defaultIfEmpty, filter, map, take, timeout} from 'rxjs/operators';
import {CompanyGroupService} from './company-group.service';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    apiUrl: string;
    groups: any[];
    currentUserPermissions: string[] = null;
    isOwner = false;
    private _permissionsLoaded = new BehaviorSubject<any>(undefined);
    private _multiSubject = new BehaviorSubject<any>(undefined);
    private _subject = new BehaviorSubject<any>(undefined);
    private types = {
        'app.dashboard': 'companies.#_dashboard',
        'creditRating': 'credit_ratings.#_creditrating',
        'oauth2': 'oauth2.#_application',
        'app.transactions': ['transactions.#_transaction', 'categories.#_category', 'categories.#_mappingrule'],
        'transactionsNew': 'transactions.#_transaction',
        'accountReceivables': 'invoices.#_invoice',
        'invoices': ['invoices.#_invoice', 'categories.#_mappingrule', 'transactions.#_transaction'],
        'transactionsMapped': 'transactions.#_transaction',
        'transactionsCategories': 'categories.#_category',
        'contracts' : ['plans.#_budget', 'contracts.#_contract'],
        'contractsPage': 'contracts.#_contract',
        'loancontract': 'contracts.#_loancontract',
        'mappingrule': 'categories.#_mappingrule',
        'app.planning': ['transactions.#_transaction_statistics', 'plans.#_plan', 'plans.#_forecast'],
        'transactionStatistics': 'transactions.#_transaction_statistics',
        'actuals': 'transactions.#_transaction',
        'planning': 'plans.#_plan',
        'committedPlans': 'plans.#_committed_plan',
        'scenario': 'plans.#_scenario',
        'forecast': 'plans.#_forecast',
        'app.reporting': 'reports.#_report',
        'app.dataRoom': ['reports.#_report', 'storage.#_storage'],
        'app.setup': ['companies.#_company', 'openbanking.#_openbankingaccount', 'openbanking.#_openbankinguser',
            'openbanking.#_finapi_bankconnection', 'openbanking.#_banksapi_bankconnection', 'openbanking.#_ebics_bankconnection',
            'openbanking.#_gocardless_bankconnection',
            'companies.#_teammember', 'companies.#_integrations', 'oauth2.#_application', 'transactions.#_account', 'allowNestedCompanyAddon', 'multipleGroups'],
        'setupCompanyInfo': 'companies.#_company',
        'setupBankAccounts': 'openbanking.#_openbankingaccount',
        'setupConnectedAccounts': 'openbanking.#_openbankinguser',
        'setupTeam': 'companies.#_teammember',
        'setupIntegration': 'companies.#_integrations',
        'connectIntegration': 'integrations.#_serviceconnection',
        'connectOneIntegration': 'integrations.#_1_connection',
        'connectUnlimitedIntegration': 'integrations.#_unlimited_connections',
        'connectBillsWalletAi': 'integrations.connect_bills_wallet_ai',
        'integrations': ['companies.#_integrations', 'oauth2.#_application', 'monite.#_moniteentity'],
        'userProfileAccount': 'billing.#_subscription',
        'subscription': 'billing.#_subscription',
        'manage_addon': 'billing.manage_addon',
        'manageAddons': ['subscription', 'manage_addon'],
        'app.settingsAccount': ['billing.#_subscription', 'multipleGroups'],
        'allowNestedCompanyAddon': 'groups.nested_companies_addon',
        'multipleGroups': 'groups.add_multiple_groups',
        'app.insights': ['transactions.#_transaction', 'invoices.#_invoice', 'plans.#_budget', 'categories.#_mappingrule'],
        'moniteentity': 'monite.#_moniteentity',
        'moniteApprovalPolicy': 'monite.#_approval_policy',
        'monitePaymentRun': 'monite.#_payment_run',
        'payables': ['monite.#_moniteentity', 'monite.#_all_bills', 'monite.#_pending_approval'],
        'bankCards': ['banking.#_card', 'banking.#_transaction', 'banking.#_all_cards', 'banking.#_own_card'],
        'activityentry': 'activity_feeds.#_activityentry',
        'comment': 'comments.#_comment',
        'feed': ['activity_feeds.#_activityentry', 'comments.#_comment'],
        'payments': 'payments.#_payment_entry',
        'advancedIntegrations': 'integrations.advanced_permissions',
        'inviteSupport': 'users.allow_invite_support_user',
        'inviteConsulting': 'users.allow_invite_consulting_user',
        'receiveWeeklyReport': 'transactions.receive_weekly_report'
    };

    private ownerPermissionExclusionKeys = ['payables', 'bankCards', 'feed'];

    //TODO: not exist in groups
    //this.isOwner = typeof company.currentUserRole === 'string';
    //this.permissions = company.currentUserRole.permissions || [];
    loadPermissionsSubscription: Subscription;
    constructor(public http: HttpClient,
                private settings: SettingsService,
                private storage: StorageService,
                private groupCompanyService: CompanyGroupService) {
        this.apiUrl = this.settings.apiUrl;
        this._permissionsLoaded = new BehaviorSubject<any>(undefined);
        this._multiSubject = new BehaviorSubject<any>(undefined);
        this._subject = new BehaviorSubject<any>(undefined);
        this.loadPermissions().subscribe(() => {
        });
    }

    checkPermission(key: string, fromMultiple?: boolean, additional?: string[]) {
        let type = fromMultiple ? key : this.types[key] || '';
        let data = {
            view: this._checkPermissionByType(type, 'view'),
            write: this._writePermissionForModelType(type, key),
            delete: this._checkPermissionByType(type, 'delete'),
            key: key
        };
        if (additional) {
            additional.forEach((str: string) => {
                let addType = this.types[str] || '';
                data[addType.split('#_')[1]] = this.checkPermission(addType, true);
            });
        }
        return data;
    }

    checkMultiplePermission(key, additional?: string[], strict?: boolean) {
        let types = this.types[key] || [];
        let multiple;
        multiple = {
            view: this.isOwner && !this.ownerPermissionExclusionKeys.includes(key) && !strict,
            write: this.isOwner && !this.ownerPermissionExclusionKeys.includes(key) && !strict,
            delete: this.isOwner && !this.ownerPermissionExclusionKeys.includes(key) && !strict,
            key: key
        };

        let permissions = types.map((type) => {
            let model = this.getPermisionKeyValue(type);
            let result = this.checkPermission(model.value, true);
            multiple[model.key || type] = result;
            return result;
        });
        ['view', 'write', 'delete'].forEach((permission) => {
            multiple[permission] = strict
                ? multiple[permission] || permissions.every((perm) => perm[permission])
                : (multiple[permission] || permissions.some((perm) => perm[permission]));
        });

        if (additional) {
            additional.forEach((str: string) => {
                let type = this.types[str] || '';
                let model = this.getPermisionKeyValue(type);
                multiple[model.key || str] = this.checkPermission(model.value, true);
            });
        }
        return multiple;
    }

    getPermisionKeyValue(type: string) {
        let value = type;
        let key;
        if (!type.includes('#_') && this.types[type]) {
            value = this.types[type];
            key = type;
        } else {
            key = type.split('#_')[1];
        }
        return {key, value}
    }

    getPermission(key: string, additional?: string[]) {
        if (this._permissionsLoaded && this._permissionsLoaded.value && (this.isOwner || this.currentUserPermissions)) {
            if (this.isOwner || this.currentUserPermissions) {
                this._subject.next(this.checkPermission(key, false, additional));
            }
        } else {
            this.loadPermissions().subscribe((permission) => {
                if (this.isOwner || permission) {
                    this._subject.next(this.checkPermission(key, false, additional));
                }
            });
        }

        return this._subject.asObservable();
    }

    getMultiplePermission(key: string, additional?: string[], strict?: boolean) {
        if (this._permissionsLoaded && this._permissionsLoaded.value && (this.isOwner || this.currentUserPermissions)) {
            if (this.isOwner || this.currentUserPermissions) {
                this._multiSubject.next(this.checkMultiplePermission(key, additional, strict));
            }
        } else {
            this.loadPermissions().subscribe((permission) => {
                if (this.isOwner || permission) {
                    this._multiSubject.next(this.checkMultiplePermission(key, additional, strict));
                }
            });
        }

        return this._multiSubject.asObservable();
    }

    /** Role name for the current company/group context, as resolved by {@link loadPermissions}. */
    get currentRole(): string {
        return this._permissionsLoaded?.value?.role || '';
    }

    /** Id of the company/group the app is currently pointed at (level 0 = tenant). */
    private get _contextId(): number {
        const level = this.storage.selectedLevel;
        return level?.id ?? this.storage.companyId;
    }

    /** True when role permissions have been resolved for the current company context. */
    isPermissionsReady(): boolean {
        const loaded = this._permissionsLoaded?.value;
        if (!loaded || !(this.isOwner || this.currentUserPermissions)) {
            return false;
        }
        // A company/group switch replays the previous context's permissions until the reload lands.
        const contextId = this._contextId;
        return contextId == null || loaded.id === contextId;
    }

    /**
     * Emits once when permissions are ready. Triggers {@link loadPermissions} when not yet loaded.
     * Prefer this over reading private `_permissionsLoaded` from feature bootstrap code.
     */
    awaitPermissionsReady$(): Observable<void> {
        if (this.isPermissionsReady()) {
            return of(undefined);
        }
        return this.loadPermissions().pipe(
            // Not `v !== undefined`: the BehaviorSubject replays the previous context's value.
            filter(() => this.isPermissionsReady()),
            take(1),
            timeout(15000),
            map(() => undefined),
            catchError(() => of(undefined)),
            defaultIfEmpty(undefined)
        );
    }

    /**
     * Emits the current permission snapshot for `key` once (immediately if permissions are
     * already loaded, else after they become ready). Prefer this over hand-rolling
     * `checkPermission(key)` + `awaitPermissionsReady$().subscribe(...)` to patch the cold-reload
     * race where a component reads permissions before {@link loadPermissions} has resolved.
     */
    checkPermissionReactive$(key: string): Observable<any> {
        return this.awaitPermissionsReady$().pipe(map(() => this.checkPermission(key)));
    }

    loadPermissions(force?: boolean): any {
        this.loadPermissionsSubscription = this.groupCompanyService.getCurrent().subscribe((data) => {
            if (data) {
                let loaded = (this._permissionsLoaded?.value !== undefined
                    && data.id === this._permissionsLoaded?.value.id
                    && data.getRoleName()=== this._permissionsLoaded?.value.role)
                    && !force;
                if (!loaded) {
                    this.isOwner = false;
                    this.currentUserPermissions = [];
                    this.isOwner = data.getRoleName() === 'Owner';

                    this.currentUserPermissions = data.currentUserPermissions || [];
                    this._permissionsLoaded.next({id: data.id, role: data.getRoleName()});
                }
            } else if (this.storage.selectedLevel && this.storage.selectedLevel.id === 0 ) {
                let loaded = this._permissionsLoaded?.value !== undefined;
                if (!loaded) {
                    this.isOwner = this.storage.model === 'tenant';

                    this._permissionsLoaded.next({id: 0, role: 'Owner'});
                }
            } else {
                this._permissionsLoaded.next(undefined);
            }
        });
        return this._permissionsLoaded.asObservable();
    }

    /**
     * For most models, UI "write" means `add_*`. Loan contracts also need `change_*`: editors often have
     * change without add; tags on active loans use change-only updates.
     */
    private _writePermissionForModelType(type: string, permissionKey: string): boolean {
        if (type === 'contracts.#_loancontract' || type === 'transactions.#_account') {
            return this._checkPermissionByType(type, 'add') || this._checkPermissionByType(type, 'change');
        }
        const addOrChange =
            permissionKey === 'committedPlans' || permissionKey === 'invitations' ? 'change' : 'add';
        return this._checkPermissionByType(type, addOrChange);
    }

    private _checkPermissionByType(type: string, key: string) {
        if (type === 'plans.#_committed_plan' && key === 'add') {
            key = 'change';
        }
        if (this.isOwner) {
            return this.currentUserPermissions ? this.currentUserPermissions.indexOf(type.replace('#', key)) >= 0 : true;
        }
        if (this.currentUserPermissions && this.currentUserPermissions.indexOf(type.replace('#', key)) >= 0) {
            return true;
        }
        return false;
    }
}
